import { Add, Check, Close, Remove } from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, CircularProgress, MenuItem, Modal, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { CompanyData, CompanyResource } from '../../../../resources';
import { useCompanyList } from './useCompanyList';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function AdminCreditModal({ open, onClose }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const { items: companyList, refetch: refetchCompanyList } = useCompanyList();
  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);

  const [tokenCount, setTokenCount] = useState(0);
  const [purchasedTokenCount, setPurchasedTokenCount] = useState(0);
  const totalCredits = tokenCount + purchasedTokenCount;

  useEffect(() => {
    if (companyList.length > 0 && !selectedCompany) {
      setSelectedCompany(companyList[0]);
    }
  }, [companyList, selectedCompany]);

  useEffect(() => {
    setTokenCount(selectedCompany?.tokenCount ?? 0);
    setPurchasedTokenCount(selectedCompany?.purchasedTokenCount ?? 0);
  }, [selectedCompany]);

  function updateCredits() {
    if (!selectedCompany) return;
    setIsLoading(true);
    // Calculate the difference between the current and new values
    CompanyResource.updateCredits(
      selectedCompany.id,
      tokenCount - selectedCompany.tokenCount,
      purchasedTokenCount - selectedCompany.purchasedTokenCount
    )
      .then(() => {
        onClose();
      })
      .finally(() => {
        refetchCompanyList();
        setIsLoading(false);
      });
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          minWidth: 700,
          maxWidth: 800,
        }}
      >
        <Button
          onClick={() => {
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            minWidth: 'auto',
            p: 1,
            borderRadius: '100%',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Close />
        </Button>
        {/* Header */}
        <Box display="flex" gap={2}>
          <Box>
            <Typography variant="h6" fontWeight={600} mb={1}>
              {selectedCompany?.name} has {totalCredits} credits.
            </Typography>
            <Box display="flex" alignItems="center" mb={2}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                Free credits will always be used first.
              </Typography>
            </Box>
          </Box>
          <Box>
            <Select
              value={selectedCompany?.id}
              onChange={(e) => {
                setSelectedCompany(companyList.find((c) => c.id === e.target.value) ?? null);
              }}
            >
              {companyList.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        {/* Cards */}
        <Box display="flex" gap={2} mb={3}>
          {/* VIP Free Credits Card */}
          <Box
            flex={1}
            sx={{
              bgcolor: 'background.2',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: 1,
            }}
          >
            <CardGiftcardIcon
              sx={{ fontSize: 20, color: 'primary.main', mb: 1, alignSelf: 'flex-start' }}
            />

            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, minWidth: 'auto', p: 1 }}
                onClick={() => {
                  setTokenCount(tokenCount - 1);
                }}
              >
                <Remove />
              </Button>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="h3" fontWeight={700} mb={0.5}>
                  {tokenCount}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  VIP free credits
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, minWidth: 'auto', p: 1 }}
                onClick={() => {
                  setTokenCount(tokenCount + 1);
                }}
              >
                <Add />
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={0.5} className="h-10">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Resets to 3 credits monthly on the 1st (JST)
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: 'background.1',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                mt: 1,
                width: '100%',
                textAlign: 'left',
              }}
            >
              <Typography variant="caption" color="text.secondary" className="flex justify-between">
                <span style={{ fontWeight: 600 }}>Expires:</span>
                <span style={{ marginLeft: 8, color: 'text.secondary' }}>
                  {new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    0
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </Typography>
            </Box>
          </Box>
          {/* Paid Credits Card */}
          <Box
            flex={1}
            sx={{
              bgcolor: 'background.2',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: 1,
            }}
          >
            <AccountBalanceWalletIcon
              sx={{ fontSize: 20, color: 'primary.main', mb: 1, alignSelf: 'flex-start' }}
            />
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, minWidth: 'auto', p: 1 }}
                onClick={() => {
                  setPurchasedTokenCount(purchasedTokenCount - 1);
                }}
              >
                <Remove />
              </Button>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="h3" fontWeight={700} mb={0.5}>
                  {purchasedTokenCount}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Paid credits
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, minWidth: 'auto', p: 1 }}
                onClick={() => {
                  setPurchasedTokenCount(purchasedTokenCount + 1);
                }}
              >
                <Add />
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={0.5} className="h-10">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Roll over indefinitely
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: 'background.1',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                mt: 1,
                width: '100%',
                textAlign: 'left',
              }}
            >
              <Typography variant="caption" color="text.secondary" className="flex justify-between">
                <span style={{ fontWeight: 600 }}>Status:</span>
                <span style={{ marginLeft: 8, color: 'text.secondary' }}>&#8734; Permanent</span>
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Add Credits Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: 16 }}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: 16 }}
            onClick={() => {
              updateCredits();
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress /> : <Check />} Add credits
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
